/**
 * Trending Reposts Component
 * Shows most reposted FoodCards in the network
 */
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Repeat2, TrendingUp, Users, ChevronRight, Sparkles } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Avatar, AvatarImage, AvatarFallback } from './ui/avatar';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TrendingReposts = ({ className = '' }) => {
  const [trending, setTrending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchTrending = async () => {
      try {
        const res = await fetch(`${API_URL}/api/foodcard/trending/reposts?limit=6`);
        if (res.ok) {
          const data = await res.json();
          setTrending(data.trending || []);
        }
      } catch (e) {
        setError('Could not load trending');
      } finally {
        setLoading(false);
      }
    };

    fetchTrending();
  }, []);

  if (loading) {
    return (
      <Card className={`overflow-hidden ${className}`}>
        <CardContent className="p-5">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 bg-gray-200 rounded-full animate-pulse" />
            <div className="h-5 bg-gray-200 rounded w-32 animate-pulse" />
          </div>
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="flex items-center gap-3 animate-pulse">
                <div className="w-16 h-16 bg-gray-200 rounded-xl" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-gray-200 rounded w-3/4" />
                  <div className="h-3 bg-gray-200 rounded w-1/2" />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (trending.length === 0) {
    return (
      <Card className={`overflow-hidden ${className}`}>
        <CardContent className="p-5 text-center">
          <div className="w-12 h-12 bg-gradient-to-br from-emerald-100 to-emerald-50 rounded-full flex items-center justify-center mx-auto mb-3">
            <Repeat2 className="w-6 h-6 text-emerald-500" />
          </div>
          <h3 className="font-bold text-gray-900 mb-1">Trending Reposts</h3>
          <p className="text-sm text-gray-500 mb-3">
            No trending reposts yet. Be the first to repost a FoodCard!
          </p>
          <Link 
            to="/recommendations"
            className="inline-flex items-center gap-1 text-sm text-emerald-600 hover:text-emerald-700 font-medium"
          >
            <Sparkles className="w-4 h-4" />
            Discover FoodCards
            <ChevronRight className="w-4 h-4" />
          </Link>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={`overflow-hidden ${className}`} data-testid="trending-reposts-card">
      <CardContent className="p-5">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gradient-to-br from-emerald-100 to-emerald-50 rounded-full flex items-center justify-center">
              <Repeat2 className="w-4 h-4 text-emerald-500" />
            </div>
            <div>
              <h3 className="font-bold text-gray-900 text-sm">Trending Reposts</h3>
              <p className="text-xs text-gray-400">Most shared FoodCards</p>
            </div>
          </div>
          <div className="flex items-center gap-1 text-xs text-emerald-600 bg-emerald-50 px-2 py-1 rounded-full">
            <TrendingUp className="w-3 h-3" />
            Hot
          </div>
        </div>

        {/* Trending Items */}
        <div className="space-y-3">
          {trending.slice(0, 5).map((item, idx) => (
            <TrendingItem key={item.post_id} item={item} rank={idx + 1} />
          ))}
        </div>

        {/* View All Link */}
        {trending.length > 3 && (
          <Link 
            to="/recommendations"
            className="mt-4 flex items-center justify-center gap-1 text-sm text-gray-500 hover:text-emerald-600 transition-colors py-2"
          >
            View all trending
            <ChevronRight className="w-4 h-4" />
          </Link>
        )}
      </CardContent>
    </Card>
  );
};

const TrendingItem = ({ item, rank }) => {
  const post = item.post || {};
  const fallbackImage = 'https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=150&q=75';

  return (
    <div 
      className="flex items-center gap-3 p-2 -mx-2 rounded-xl hover:bg-gray-50 transition-colors cursor-pointer group"
      data-testid={`trending-item-${rank}`}
    >
      {/* Rank Badge */}
      <div className={`w-6 h-6 flex-shrink-0 rounded-full flex items-center justify-center text-xs font-bold ${
        rank === 1 ? 'bg-amber-100 text-amber-600' :
        rank === 2 ? 'bg-gray-100 text-gray-600' :
        rank === 3 ? 'bg-orange-100 text-orange-600' :
        'bg-gray-50 text-gray-400'
      }`}>
        {rank}
      </div>

      {/* Image */}
      <div className="relative w-14 h-14 flex-shrink-0">
        <img 
          src={post.image_url || fallbackImage}
          alt={post.dish_name || 'FoodCard'}
          className="w-full h-full object-cover rounded-xl"
          onError={(e) => { e.target.src = fallbackImage; }}
        />
        {/* Repost count badge */}
        <div className="absolute -bottom-1 -right-1 bg-emerald-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full flex items-center gap-0.5">
          <Repeat2 className="w-2.5 h-2.5" />
          {item.repost_count}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <h4 className="text-sm font-semibold text-gray-900 truncate group-hover:text-emerald-600 transition-colors">
          {post.dish_name || post.caption?.slice(0, 30) || 'FoodCard'}
        </h4>
        {post.venue_name && (
          <p className="text-xs text-gray-500 truncate">{post.venue_name}</p>
        )}
        
        {/* Reposters avatars */}
        {item.reposters && item.reposters.length > 0 && (
          <div className="flex items-center gap-1 mt-1">
            <div className="flex -space-x-2">
              {item.reposters.slice(0, 3).map((r, i) => (
                <Avatar key={i} className="w-5 h-5 border-2 border-white">
                  <AvatarFallback className="bg-gradient-to-br from-[#F4C430] to-[#FF6B35] text-white text-[8px]">
                    {r.user_name?.[0]?.toUpperCase() || 'F'}
                  </AvatarFallback>
                </Avatar>
              ))}
            </div>
            {item.reposters.length > 3 && (
              <span className="text-[10px] text-gray-400">
                +{item.reposters.length - 3} more
              </span>
            )}
          </div>
        )}
      </div>

      {/* Arrow */}
      <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-emerald-500 transition-colors flex-shrink-0" />
    </div>
  );
};

export default TrendingReposts;
